import { Explicacao } from '../../../components/Explicacao'
import { Cartao } from '../../../components/ListaDeDados'
import { cn } from '../../../lib/cn'
import { reais } from '../../../lib/dinheiro'
import type { DetalheDaOs, ItemDaOs } from '../../../types/ordemDeServico'
import { TERMOS_DA_OS } from '../termosDaOs'
import { OS } from '../textosDaOs'

const CELULA = 'px-3 py-2 align-top text-sm text-slate-700'

/**
 * A seção 4 do modelo: as linhas de serviço, com a conferência do sistema ao lado do documento.
 *
 * **A tela não recalcula dinheiro, e isto é a regra mais importante do arquivo.** `valorTotalDerivado`
 * e `divergencia` chegam prontos do servidor, em `BigDecimal` com `HALF_UP`. Refazer a conta aqui
 * seria fazê-la em IEEE-754: `Math.round(1.005 * 100) / 100` dá 1,00 onde o servidor dá 1,01 — e a
 * divergência inventada apareceria justamente no campo cujo propósito é denunciar divergência.
 *
 * **Os dois números aparecem, e nenhum é corrigido.** A prática do contrato arredonda cada linha
 * antes de somar: em maio/2026, 182.870,37 + 40.277,78 = 223.148,15, enquanto arredondar a soma dá
 * 223.148,14 — um centavo. O documento foi assinado como está, e quem decide qual número vale é
 * quem apura, não a tela. Um CHECK de igualdade no banco chegou a ser proposto e foi rejeitado: ele
 * transformaria "o papel arredondou diferente" em documento que o sistema recusa registrar.
 *
 * **A quantidade sai como fração.** "25/30" é como o termo de recebimento a escreve; `0,83` perderia
 * R$ 731,48 na linha de sustentação e `0,8333…` mentiria sobre o documento.
 *
 * @param props a ordem carregada
 * @returns o quadro dos itens
 */
export function ItensDaOs({ ordem }: { ordem: DetalheDaOs }) {
  const algumDivergente = ordem.itens.some((item) => item.divergente)

  return (
    <Cartao titulo={OS.abas.itens} descricao="Seção 4 do modelo do contrato">
      <div className="overflow-x-auto">
        <table className="min-w-full text-left">
          <caption className="sr-only">
            As linhas de serviço da ordem, com o valor declarado no documento e a conferência do sistema
          </caption>
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50 text-xs font-semibold text-slate-600">
              <th scope="col" className="px-3 py-2 font-semibold">
                Item
              </th>
              <th scope="col" className="px-3 py-2 font-semibold">
                Serviço
              </th>
              <th scope="col" className="px-3 py-2 text-right font-semibold">
                Valor unitário
              </th>
              <th scope="col" className="px-3 py-2 font-semibold">
                <span className="inline-flex items-center gap-1">
                  {TERMOS_DA_OS.quantidadeComoFracao.rotulo}
                  <Explicacao
                    titulo={TERMOS_DA_OS.quantidadeComoFracao.rotulo}
                    texto={TERMOS_DA_OS.quantidadeComoFracao.ajuda}
                    className="h-3.5 w-3.5"
                  />
                </span>
              </th>
              <th scope="col" className="px-3 py-2 text-right font-semibold">
                {OS.totalDeclarado}
              </th>
              <th scope="col" className="px-3 py-2 text-right font-semibold">
                {OS.totalDerivado}
              </th>
            </tr>
          </thead>
          <tbody>
            {ordem.itens.map((item) => (
              <Linha key={item.item} item={item} />
            ))}
          </tbody>
        </table>
      </div>

      <Totais ordem={ordem} />

      {algumDivergente && <p className="mt-3 max-w-prose text-sm text-slate-600">{OS.divergenciaAjuda}</p>}
    </Cartao>
  )
}

/** Uma linha da seção 4. A fração é impressa como fração — nunca como decimal. */
function Linha({ item }: { item: ItemDaOs }) {
  return (
    <tr className="border-b border-slate-100 align-top">
      <td className={cn(CELULA, 'tabular-nums')}>{item.item}</td>
      <td className={CELULA}>
        {item.descricao}
        <span className="block text-xs text-slate-500">
          {item.metrica === 'PONTO_DE_FUNCAO' ? 'Ponto de Função' : 'Valor fixo'}
          {item.unidade ? ` · ${item.unidade}` : ''}
          {item.catser ? ` · CATSER ${item.catser}` : ''}
        </span>
      </td>
      <td className={cn(CELULA, 'whitespace-nowrap text-right tabular-nums')}>{reais(item.valorUnitario)}</td>
      <td className={cn(CELULA, 'whitespace-nowrap tabular-nums')}>{item.quantidade}</td>
      <td className={cn(CELULA, 'whitespace-nowrap text-right font-medium tabular-nums text-slate-900')}>
        {reais(item.valorTotalDeclarado)}
      </td>
      <td className={cn(CELULA, 'whitespace-nowrap text-right tabular-nums')}>
        {reais(item.valorTotalDerivado)}
        {item.divergente && (
          <span className="mt-1 block text-xs font-medium text-amber-800">
            {OS.divergenciaDoItem(reais(item.valorTotalDeclarado) ?? '', reais(item.valorTotalDerivado) ?? '')}
          </span>
        )}
      </td>
    </tr>
  )
}

/**
 * O rodapé do documento e a soma das linhas, lado a lado.
 *
 * São dois números com procedências diferentes: `valorTotal` é o "Valor total estimado da OS" que o
 * documento digitou, e `totalDosItens` é a soma das linhas que ele declarou. Quando divergem, os
 * dois ficam à vista — reconciliar em silêncio esconderia justamente o que a conferência serve para
 * mostrar.
 */
function Totais({ ordem }: { ordem: DetalheDaOs }) {
  const divergeNoTotal = ordem.divergenciaDoTotal !== 0
  return (
    <dl className="mt-4 grid gap-x-6 gap-y-1 border-t border-slate-200 pt-3 text-sm sm:grid-cols-2">
      <Total rotulo={`${OS.totalDeclarado} (rodapé)`} valor={reais(ordem.valorTotal) ?? ''} destacado />
      <Total rotulo="Soma das linhas declaradas" valor={reais(ordem.totalDosItens) ?? ''} />
      {divergeNoTotal && (
        <p className="mt-2 max-w-prose text-sm text-amber-800 sm:col-span-2">
          {OS.divergenciaDoItem(reais(ordem.valorTotal) ?? '', reais(ordem.totalDosItens) ?? '')}{' '}
          {OS.divergenciaAjuda}
        </p>
      )}
    </dl>
  )
}

/** Uma linha do rodapé de totais. */
function Total({ rotulo, valor, destacado }: { rotulo: string; valor: string; destacado?: boolean }) {
  return (
    <div className="flex flex-wrap justify-between gap-x-3 py-1">
      <dt className="text-slate-600">{rotulo}</dt>
      <dd className={destacado ? 'font-semibold tabular-nums text-slate-900' : 'tabular-nums text-slate-800'}>
        {valor}
      </dd>
    </div>
  )
}
